/**
 * lib/statementPdf.ts
 * Client account statement: opening balance, dated debits/credits with
 * a running balance, and the closing balance due.
 */
import { jsPDF } from 'jspdf';
import type { CompanySettings, ClientSnapshot } from '@/types';
import { formatPula } from './money';
import { drawBand, drawCompanyBlock, fmtDate, drawBankingDetails, PDF_CHAR, PDF_GRAY, PDF_LINE, PAGE_W, MARGIN } from './pdfHeader';
import type { StatementResult } from '@/features/statements/statementData';

export interface StatementMeta {
  client: ClientSnapshot;
  from?: string;
  to?: string;
  generatedOn: string;
}

export function statementFileName(client: ClientSnapshot, to?: string): string {
  const safe = client.name.replace(/[^\w]+/g, '-');
  return `Statement-${safe}${to ? `-${to}` : ''}.pdf`;
}

export function buildStatementPdf(data: StatementResult, meta: StatementMeta, company: CompanySettings): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = PAGE_W;
  const M = MARGIN;
  const right = W - M;

  drawBand(doc, company, 'STATEMENT');
  let y = drawCompanyBlock(doc, company, 48);

  // Statement-to (right column)
  let my = 48;
  const metaRows: Array<[string, string]> = [];
  if (meta.from || meta.to) metaRows.push(['Period', `${meta.from ? fmtDate(meta.from) : 'Start'} – ${meta.to ? fmtDate(meta.to) : 'Date'}`]);
  metaRows.push(['Generated', fmtDate(meta.generatedOn)]);
  doc.setFontSize(11);
  metaRows.forEach(([k, v]) => {
    doc.setTextColor(...PDF_GRAY);
    doc.setFont('helvetica', 'normal');
    doc.text(k, right - 66, my);
    doc.setTextColor(...PDF_CHAR);
    doc.setFont('helvetica', 'bold');
    doc.text(v, right, my, { align: 'right' });
    my += 5.5;
  });

  y = Math.max(y, my) + 6;
  doc.setTextColor(...PDF_GRAY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('STATEMENT FOR', M, y);
  y += 5.5;
  doc.setTextColor(...PDF_CHAR);
  doc.setFontSize(11.5);
  doc.text(meta.client.name, M, y);
  y += 5.5;
  if (meta.client.billingAddress) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(...PDF_GRAY);
    doc.text(meta.client.billingAddress, M, y);
    y += 5;
  }

  // Table header
  y += 6;
  const cDate = M + 2;
  const cRef = M + 30;
  const cDesc = M + 60;
  const cDebit = right - 73;
  const cCredit = right - 38;
  const cBal = right - 2;
  doc.setFillColor(255, 241, 234);
  doc.rect(M, y - 5, right - M, 9, 'F');
  doc.setTextColor(...PDF_CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('DATE', cDate, y);
  doc.text('REF', cRef, y);
  doc.text('DESCRIPTION', cDesc, y);
  doc.text('DEBIT', cDebit, y, { align: 'right' });
  doc.text('CREDIT', cCredit, y, { align: 'right' });
  doc.text('BALANCE', cBal, y, { align: 'right' });
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  // Opening balance row
  doc.setTextColor(...PDF_GRAY);
  doc.text('Opening balance', cDesc, y);
  doc.setTextColor(...PDF_CHAR);
  doc.text(formatPula(data.opening), cBal, y, { align: 'right' });
  y += 5.5;
  doc.setDrawColor(...PDF_LINE);
  doc.line(M, y - 3, right, y - 3);

  data.entries.forEach((e) => {
    if (y > 270) {
      doc.addPage();
      y = 24;
    }
    doc.setTextColor(...PDF_GRAY);
    doc.text(fmtDate(e.date), cDate, y);
    doc.setTextColor(...PDF_CHAR);
    doc.text(e.ref, cRef, y);
    doc.text(e.description, cDesc, y);
    if (e.debit) doc.text(formatPula(e.debit), cDebit, y, { align: 'right' });
    if (e.credit) doc.text(formatPula(e.credit), cCredit, y, { align: 'right' });
    doc.text(formatPula(e.balance), cBal, y, { align: 'right' });
    y += 5.5;
    doc.setDrawColor(...PDF_LINE);
    doc.line(M, y - 3, right, y - 3);
  });

  // Closing balance
  y += 5;
  doc.setFillColor(255, 241, 234);
  doc.rect(cDebit - 10, y - 5, right - (cDebit - 10), 9, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12.5);
  doc.setTextColor(...PDF_CHAR);
  doc.text('Balance due', cDebit - 6, y + 1);
  doc.text(formatPula(data.closing), cBal, y + 1, { align: 'right' });

  if (company.bank) {
    drawBankingDetails(doc, company.bank, M, y + 14);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...PDF_GRAY);
  doc.text('Generated by Acepsle Hub. Please settle any outstanding balance at your earliest convenience.', M, 290);

  return doc;
}

export function downloadStatementPdf(data: StatementResult, meta: StatementMeta, company: CompanySettings): void {
  buildStatementPdf(data, meta, company).save(statementFileName(meta.client, meta.to));
}

export function statementPdfBase64(data: StatementResult, meta: StatementMeta, company: CompanySettings): string {
  return buildStatementPdf(data, meta, company).output('datauristring').split(',')[1] ?? '';
}
